import {
  deleteFile,
  getPortfolioFileContentBlob,
  listPeriods,
  listPortfolioFileCatalog,
  type CoworkPeriod,
  type CoworkFileCatalog,
  type CoworkPortfolioFile,
} from "@/cowork/api"
import { AccountingImportButton } from "@/components/accounting-import"
import { canUploadDocuments } from "@/cowork/document-upload"
import type { BookImportAttention } from "@/cowork/api"
import { useCoworkWorkspace } from "@/cowork/workspace-context"
import { collectionPath, useCollectionScope } from "@/cowork/scope-url"
import { useAppNavigate } from "@/cowork/workspace-route"
import { useSearchParams } from "@solidjs/router"
import { createFileArchive } from "@/cowork/file-archive"
import { Spinner } from "@opencode-ai/ui/spinner"
import { useLanguage } from "@/context/language"
import { dateFormatter } from "@/utils/date"
import { CoworkFilePreview } from "@/components/cowork-file-preview"
import { FileCatalogFilters, documentTypeLabel, type FileCategory } from "@/components/file-catalog-filters"
import { batch, createEffect, createMemo, createResource, For, on, onCleanup, onMount, Show } from "solid-js"
import { createStore } from "solid-js/store"
import { FileIcon } from "@opencode-ai/ui/file-icon"
import { Icon as IconV2 } from "@opencode-ai/ui/v2/icon"
import { IconButtonV2 } from "@opencode-ai/ui/v2/icon-button-v2"
import { MenuV2 } from "@opencode-ai/ui/v2/menu-v2"
import { useDeleteConfirmation } from "@/components/delete-confirmation"

type DataSection = "files" | "datasets" | "extracted" | "sources"
type PeriodFolder = {
  id: string
  periodID: string
  companyID: string
  projectID: string
  name: string
  company: string
  project: string
  files: CoworkPortfolioFile[]
  metadata?: CoworkPeriod
}

export function CoworkFilesPage() {
  const routeScope = useCollectionScope()
  const context = useCoworkWorkspace()
  const [query, setQuery] = useSearchParams()
  const scope = createMemo(() => {
    const explicit = routeScope()
    if (explicit.companyID || explicit.projectID || explicit.periodID) return explicit
    return {
      companyID: context.state.entityId || undefined,
      projectID: context.state.entityId ? context.state.projectId || undefined : undefined,
      periodID: context.state.entityId && context.state.projectId && context.state.periodId !== context.allPeriodsID
        ? context.state.periodId || undefined : undefined,
    }
  })
  const language = useLanguage()
  const navigate = useAppNavigate()
  return <Show when={(!scope().companyID || context.state.entityId === scope().companyID) &&
    (!scope().projectID || context.selectedProject()?.id === scope().projectID)}
    fallback={<p role="status" class="p-7">{language.t(scope().companyID && context.state.entityId !== scope().companyID
      ? "cowork.enterprise.scope.selectCompany" : "cowork.enterprise.scope.selectProject")}</p>}>
    <CoworkFilesView
      entityID={scope().companyID || "ALL"}
      projectID={scope().projectID}
      periodID={scope().periodID}
      navigateFolder={navigate}
      importRequest={typeof query.import === "string" ? query.import : undefined}
      importAttention={["review", "failed", "processing"].includes(String(query.attention)) ? query.attention as BookImportAttention : undefined}
      onImportOpened={() => setQuery({ import: undefined, attention: undefined }, { replace: true })}
    />
  </Show>
}

export function CoworkDataScopeView(props: { entityID: string; projectID?: string; periodID?: string }) {
  const language = useLanguage()
  const [state, setState] = createStore({ section: "files" as DataSection })
  const sections: DataSection[] = ["files", "datasets", "extracted", "sources"]

  return (
    <div>
      <div class="mb-5 flex gap-1 overflow-x-auto rounded-lg bg-v2-background-bg-layer-02 p-1">
        <For each={sections}>
          {(section) => (
            <button
              type="button"
              class="whitespace-nowrap rounded-md px-3 py-2 text-[11px] font-medium text-v2-text-text-muted hover:text-v2-text-text-base"
              classList={{
                "bg-v2-background-bg-base text-v2-text-text-base shadow-[var(--v2-elevation-raised)]":
                  state.section === section,
              }}
              onClick={() => setState("section", section)}
            >
              {language.t(`cowork.enterprise.data.${section}` as keyof typeof import("@/i18n/en").dict)}
            </button>
          )}
        </For>
      </div>
      <Show when={state.section === "files"}>
        <CoworkFilesView entityID={props.entityID} projectID={props.projectID} periodID={props.periodID} embedded />
      </Show>
      <Show when={state.section === "datasets"}>
        <CoworkFilesView
          entityID={props.entityID}
          projectID={props.projectID}
          periodID={props.periodID}
          embedded
          initialCategory="dataset"
        />
      </Show>
      <Show when={state.section === "extracted"}>
        <DataEmpty
          title={language.t("cowork.enterprise.data.extractedEmptyTitle")}
          description={language.t("cowork.enterprise.data.extractedEmptyDescription")}
        />
      </Show>
      <Show when={state.section === "sources"}>
        <DataEmpty
          title={language.t("cowork.enterprise.data.sourcesEmptyTitle")}
          description={language.t("cowork.enterprise.data.sourcesEmptyDescription")}
        />
      </Show>
    </div>
  )
}

export function CoworkFilesView(props: {
  entityID: string
  projectID?: string
  periodID?: string
  embedded?: boolean
  initialCategory?: FileCategory
  snapshotFiles?: readonly { id: string; name: string }[]
  navigateFolder?: (path: string) => void
  importRequest?: string
  importAttention?: BookImportAttention
  onImportOpened?: () => void
}) {
  const language = useLanguage()
  const context = useCoworkWorkspace()
  const scope = createMemo(
    () =>
      Object.freeze({
        entityID: props.entityID,
        projectID: props.projectID && props.projectID !== "ALL" ? props.projectID : undefined,
        periodID: props.periodID && props.periodID !== "ALL" ? props.periodID : undefined,
        snapshotFiles: props.snapshotFiles?.map((file) => ({ id: file.id, name: file.name })),
      }),
    undefined,
    {
      equals: (previous, next) =>
        previous.entityID === next.entityID &&
        previous.projectID === next.projectID &&
        previous.periodID === next.periodID &&
        (previous.snapshotFiles === next.snapshotFiles ||
          (previous.snapshotFiles !== undefined &&
            next.snapshotFiles !== undefined &&
            previous.snapshotFiles.length === next.snapshotFiles.length &&
            previous.snapshotFiles.every(
              (file, index) =>
                file.id === next.snapshotFiles![index].id && file.name === next.snapshotFiles![index].name,
            ))),
    },
  )
  const scopeKey = createMemo(() => JSON.stringify(scope()))
  const [state, setState] = createStore({
    search: "",
    searchQuery: "",
    documentTypes: [] as string[],
    category: props.initialCategory ?? ("all" as FileCategory),
    folder: undefined as PeriodFolder | undefined,
    preview: undefined as CoworkPortfolioFile | undefined,
    scopeKey: scopeKey(),
    deleting: "",
    error: "",
    selectedIDs: [] as string[],
    selectionKey: "",
    bulk: "" as "" | "download" | "delete",
    bulkProcessed: 0,
    bulkTotal: 0,
    bulkFailures: [] as string[],
  })
  const currentFolder = () => (state.scopeKey === scopeKey() ? state.folder : undefined)
  const confirmDelete = useDeleteConfirmation()
  const query = createMemo(
    () => {
      const selected = scope()
      const folder = currentFolder()
      const current = state.scopeKey === scopeKey()
      const options = {
        entityID: folder?.companyID ?? selected.entityID,
        projectID: folder?.projectID ?? selected.projectID,
        periodID: folder?.periodID ?? selected.periodID,
        search: current ? state.searchQuery : "",
        documentTypes: current ? [...state.documentTypes] : [],
        category: current ? state.category : (props.initialCategory ?? "all"),
        fileIDs: selected.snapshotFiles?.map((file) => file.id),
      }
      return {
        scope: selected,
        options,
        key: JSON.stringify(options),
        viewKey: JSON.stringify([scopeKey(), folder?.id ?? null]),
      }
    },
    undefined,
    {
      equals: (previous, next) =>
        previous?.scope === next.scope && previous.key === next.key && previous.viewKey === next.viewKey,
    },
  )
  type CatalogResult = CoworkFileCatalog & { scope: ReturnType<typeof scope>; key: string; viewKey: string }
  const [files, fileActions] = createResource<CatalogResult, ReturnType<typeof query>>(
    query,
    async (selected, previous) => {
      const catalog =
        selected.scope.snapshotFiles?.length === 0
          ? { items: [], total: 0, available_file_ids: [], sellers: [], document_types: [], accounting: false }
          : await listPortfolioFileCatalog(selected.options)
      const previousItems = new Map(
        (previous.value?.scope === selected.scope ? previous.value.items : []).map((file) => [file.id, file]),
      )
      return {
        ...catalog,
        scope: selected.scope,
        key: selected.key,
        viewKey: selected.viewKey,
        items: catalog.items.map((file) => {
          const prior = previousItems.get(file.id)
          return prior && JSON.stringify(prior) === JSON.stringify(file) ? prior : file
        }),
      }
    },
  )
  createEffect(
    on(
      () => [scopeKey(), state.search] as const,
      ([key, text]) => {
        const timer = setTimeout(() => {
          if (scopeKey() === key) setState("searchQuery", text.trim())
        }, 250)
        onCleanup(() => clearTimeout(timer))
      },
    ),
  )
  const catalog = createMemo<CatalogResult | undefined>((previous) => {
    const sameView = (value: CatalogResult | undefined) => value?.scope === scope() && value.viewKey === query().viewKey
    if (files.state === "ready" || files.state === "refreshing") {
      const value = files.latest
      return sameView(value) ? value : undefined
    }
    // Keep open filter menus mounted while a new filter query is pending in the same view.
    return files.state === "pending" && sameView(previous) ? previous : undefined
  })
  const periodScope = createMemo(() =>
    props.snapshotFiles === undefined &&
    props.projectID &&
    props.projectID !== "ALL" &&
    props.entityID &&
    props.entityID !== "ALL"
      ? { companyID: props.entityID, projectID: props.projectID }
      : undefined,
  )
  const [periods, periodActions] = createResource(periodScope, async (selected) => ({
    scope: selected,
    items: await listPeriods(selected.projectID, selected.companyID),
  }))
  const periodError = () => !!periodScope() && periods.error
  // Before the first accepted value (or after an error), even .latest can suspend or throw.
  const periodMetadata = createMemo(() => {
    if (periods.state !== "ready" && periods.state !== "refreshing") return []
    const value = periods.latest
    const selected = periodScope()
    if (!selected || value?.scope !== selected) return []
    return value.items.filter(
      (period) => period.company_id === selected.companyID && period.project_id === selected.projectID,
    )
  })
  const companies = createMemo(() =>
    context.companies.state === "ready" || context.companies.state === "refreshing"
      ? context.companies.latest
      : undefined,
  )
  const projects = createMemo(() =>
    context.projects.state === "ready" || context.projects.state === "refreshing" ? context.projects.latest : undefined,
  )
  createEffect(
    on(scope, (selected) =>
      setState({
        scopeKey: JSON.stringify(selected),
        folder: undefined,
        preview: undefined,
        search: "",
        searchQuery: "",
        documentTypes: [],
        category: props.initialCategory ?? "all",
        error: "",
      }),
    ),
  )
  const openFolder = (folder?: PeriodFolder) => {
    if (props.snapshotFiles === undefined && props.navigateFolder) {
      const projectID = folder?.projectID ?? scope().projectID
      const companyID = folder?.companyID ?? (scope().entityID !== "ALL" ? scope().entityID : context.selectedProject()?.company_id)
      if (companyID && projectID) {
        const periodID = folder?.periodID
        batch(() => {
          context.selectEntity(companyID)
          context.selectProject(projectID)
          context.selectPeriod(periodID ?? context.allPeriodsID)
          props.navigateFolder!(collectionPath("files", { companyID, projectID, periodID }))
        })
        return
      }
    }
    setState({
      folder,
      search: "",
      searchQuery: "",
      documentTypes: [],
      category: props.initialCategory ?? "all",
    })
  }
  let alive = true
  onCleanup(() => {
    alive = false
  })
  let root: HTMLDivElement | undefined
  const refresh = () => {
    if (
      !alive ||
      files.loading ||
      state.bulk ||
      state.preview ||
      document.visibilityState !== "visible" ||
      !root?.getClientRects().length
    )
      return
    void Promise.resolve(fileActions.refetch()).catch(() => {})
  }
  onMount(() => {
    const timer = window.setInterval(refresh, 15000)
    window.addEventListener("focus", refresh)
    document.addEventListener("visibilitychange", refresh)
    onCleanup(() => {
      window.clearInterval(timer)
      window.removeEventListener("focus", refresh)
      document.removeEventListener("visibilitychange", refresh)
    })
  })
  const scopedFiles = createMemo(() => {
    if (files.state !== "ready" && files.state !== "refreshing") return []
    const value = files.latest
    const source = value?.scope === scope() && value.key === query().key ? value.items : []
    if (props.snapshotFiles === undefined) return source
    const ids = new Set(props.snapshotFiles.map((file) => file.id))
    return source.filter((file) => ids.has(file.id))
  })
  const unavailableFiles = createMemo(() => {
    if (files.state !== "ready" || files.latest?.scope !== scope()) return []
    const available = new Set(files.latest.available_file_ids)
    return (props.snapshotFiles ?? []).filter((file) => !available.has(file.id))
  })
  const filtered = scopedFiles
  const clearFilters = () =>
    setState({
      search: "",
      searchQuery: "",
      documentTypes: [],
      category: props.initialCategory ?? "all",
    })
  const hasFilters = () =>
    !!state.search ||
    state.documentTypes.length > 0 ||
    state.category !== (props.initialCategory ?? "all")
  const periodGroups = createMemo(() => {
    if (props.snapshotFiles !== undefined) return []
    const groups = new Map<string, PeriodFolder>()
    for (const file of scopedFiles()) {
      const periodID = file.period_id ?? file.workflow_id
      if (!periodID) continue
      const key = filePeriodKey(file)
      const group = groups.get(key) ?? {
        id: key,
        periodID,
        companyID: file.company_id,
        projectID: file.project_id,
        name: file.period_name || language.t("cowork.enterprise.files.unnamedPeriod"),
        company: file.company_name,
        project: file.project_name,
        files: [],
      }
      group.files.push(file)
      groups.set(key, group)
    }
    for (const period of periodMetadata()) {
      if (scope().periodID && period.id !== scope().periodID) continue
      const key = JSON.stringify([period.company_id, period.project_id, period.id])
      const existing = groups.get(key)
      groups.set(key, {
        id: key,
        periodID: period.id,
        companyID: period.company_id,
        projectID: period.project_id,
        name: period.name || language.t("cowork.enterprise.files.unnamedPeriod"),
        company:
          existing?.company ||
          companies()?.find((company) => company.id === period.company_id)?.name ||
          period.company_id,
        project:
          existing?.project ||
          projects()?.find((project) => project.id === period.project_id && project.company_id === period.company_id)
            ?.name ||
          period.project_id,
        files: existing?.files ?? [],
        metadata: period,
      })
    }
    return [...groups.values()]
  })
  // Keep the selected folder during refetches, including folders with no files yet.
  const activePeriod = createMemo(() => {
    const selected = currentFolder()
    return selected ? (periodGroups().find((group) => group.id === selected.id) ?? selected) : undefined
  })
  const flatView = () => props.snapshotFiles !== undefined || !!scope().periodID || !!activePeriod()
  const visibleFiles = createMemo(() =>
    activePeriod() ? filtered().filter((file) => filePeriodKey(file) === activePeriod()!.id) : filtered(),
  )
  const looseFiles = createMemo(() => scopedFiles().filter((file) => !(file.period_id ?? file.workflow_id)))
  const selectableFiles = () => flatView() ? visibleFiles() : looseFiles()
  const selectionKey = createMemo(() => JSON.stringify([query().viewKey, query().key, state.search]))
  let selectionVersion = 0
  createEffect(on(selectionKey, (key) => {
    selectionVersion++
    setState({ selectionKey: key, selectedIDs: [], bulk: "", bulkProcessed: 0, bulkTotal: 0, bulkFailures: [], error: "" })
  }))
  const selectedSet = createMemo(() => new Set(state.selectionKey === selectionKey() ? state.selectedIDs : []))
  const selectedFiles = createMemo(() => selectableFiles().filter((file) => selectedSet().has(file.id)))
  const allSelected = () => selectableFiles().length > 0 && selectedFiles().length === selectableFiles().length
  const toggleFile = (file: CoworkPortfolioFile, checked: boolean) => {
    if (state.bulk || state.deleting || files.loading) return
    setState("selectedIDs", (ids) => checked ? [...new Set([...ids, file.id])] : ids.filter((id) => id !== file.id))
  }
  const toggleAll = (checked: boolean) => {
    if (state.bulk || state.deleting || files.loading) return
    setState("selectedIDs", checked ? selectableFiles().map((file) => file.id) : [])
  }
  const saveBlob = (blob: Blob, name: string) => {
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement("a")
    anchor.href = url
    anchor.download = name
    document.body.append(anchor)
    try { anchor.click() } finally { anchor.remove(); setTimeout(() => URL.revokeObjectURL(url), 1000) }
  }
  const runBulk = async (action: "download" | "delete") => {
    if (state.bulk || state.deleting || files.loading || (action === "delete" && props.snapshotFiles !== undefined)) return
    const targets = [...selectedFiles()]
    if (!targets.length) return
    const key = selectionKey(), version = selectionVersion
    const current = () => alive && selectionKey() === key && selectionVersion === version
    if (action === "delete") {
      const confirmCurrent = () => current() && props.snapshotFiles === undefined && !state.bulk && !state.deleting &&
        selectedFiles().length === targets.length && targets.every((target) => selectedSet().has(target.id))
      if (!await confirmDelete({
        message: targets.length === 1 ? language.t("cowork.enterprise.files.deleteConfirm", { name: targets[0].filename }) :
          language.t("cowork.enterprise.files.selectedCount", { count: targets.length }),
        items: targets.length > 1 ? targets.map((file) => file.filename) : undefined,
        valid: confirmCurrent,
      }) || !confirmCurrent()) return
    }
    const failures: CoworkPortfolioFile[] = []
    const downloads: { name: string; blob: Blob }[] = []
    const deleted = new Set<string>()
    setState({ bulk: action, bulkProcessed: 0, bulkTotal: targets.length, bulkFailures: [], error: "" })
    try {
      for (const file of targets) {
        if (!current()) return
        try {
          if (action === "download") downloads.push({ name: file.filename, blob: await getPortfolioFileContentBlob(file.id, file.company_id) })
          else {
            await deleteFile(file.project_id, file.id, file.company_id)
            deleted.add(file.id)
          }
        } catch { failures.push(file) }
        if (!current()) return
        setState("bulkProcessed", (count) => count + 1)
        if (deleted.has(file.id)) {
          setState("selectedIDs", (ids) => ids.filter((id) => id !== file.id))
          if (state.preview?.id === file.id) setState("preview", undefined)
        }
      }
      if (!current()) return
      if (action === "download" && downloads.length) {
        const archive = await createFileArchive(downloads)
        if (!current()) return
        saveBlob(archive, "files.zip")
      }
      if (action === "delete") {
        fileActions.mutate((value) => value && ({ ...value,
          items: value.items.filter((file) => !deleted.has(file.id)),
          available_file_ids: value.available_file_ids.filter((id) => !deleted.has(id)),
          total: Math.max(0, value.total - deleted.size),
        }))
        await fileActions.refetch()
      }
      if (current() && failures.length) setState({ selectedIDs: failures.map((file) => file.id),
        bulkFailures: failures.map((file) => file.filename),
        error: language.t(action === "download" ? "cowork.enterprise.files.downloadError" : "cowork.enterprise.files.deleteError") })
    } catch {
      if (current()) setState("error", language.t(action === "download" ? "cowork.enterprise.files.downloadError" : "cowork.enterprise.files.partialError"))
    } finally {
      if (current()) setState("bulk", "")
    }
  }
  const destination = createMemo(() => {
    if (props.snapshotFiles !== undefined) return undefined
    const folder = activePeriod()
    const companyID = folder?.companyID ?? periodScope()?.companyID
    const projectID = folder?.projectID ?? periodScope()?.projectID
    if (!companyID || !projectID) return undefined
    const periodID = folder?.periodID ?? scope().periodID ?? null
    const period = folder?.metadata ?? periodMetadata().find((item) => item.id === periodID)
    if (periodID && (!period || period.id !== periodID || period.company_id !== companyID || period.project_id !== projectID)) return undefined
    const company = companies()?.find((item) => item.id === companyID)
    const project = projects()?.find((item) => item.id === projectID && item.company_id === companyID)
    return Object.freeze({
      companyID,
      projectID,
      periodID,
      locked: !!period && (period.is_locked || period.period_status === "closed" || period.period_status === "locked"),
      canUpload: canUploadDocuments(company?.role, project?.project_role, period),
      label: language.t("cowork.overview.uploadDestination", { company: company?.name ?? companyID,
        project: project?.name ?? projectID, period: period?.name ?? language.t("cowork.overview.projectFiles") }),
    })
  })
  const downloadFile = async (file: CoworkPortfolioFile) => {
    if (state.bulk) return
    const selected = scope()
    setState({ error: "", bulkFailures: [] })
    try {
      const blob = await getPortfolioFileContentBlob(file.id, file.company_id)
      if (alive && scope() === selected) saveBlob(blob, file.filename)
    } catch {
      if (alive && scope() === selected) setState("error", language.t("cowork.enterprise.files.downloadError"))
    }
  }

  const viewFile = (file: CoworkPortfolioFile) => setState({ preview: file, error: "" })

  const removeFile = async (file: CoworkPortfolioFile) => {
    if (props.snapshotFiles !== undefined || state.bulk || state.deleting || files.loading) return
    const selected = scope()
    const key = selectionKey(), version = selectionVersion
    const current = () => alive && scope() === selected && selectionKey() === key && selectionVersion === version
    if (!await confirmDelete({ message: language.t("cowork.enterprise.files.deleteConfirm", { name: file.filename }),
      valid: () => current() && props.snapshotFiles === undefined && !state.bulk && !state.deleting,
    }) || !current() || state.bulk || state.deleting) return
    setState({ deleting: file.id, error: "", bulkFailures: [] })
    try {
      await deleteFile(file.project_id, file.id, file.company_id)
      if (current() && state.preview?.id === file.id) setState("preview", undefined)
      if (current()) await fileActions.refetch()
    } catch {
      if (current()) setState("error", language.t("cowork.enterprise.files.deleteError"))
    } finally {
      if (alive) setState("deleting", "")
    }
  }

  const ImportButton = () => <AccountingImportButton scope={destination()} target="auto" recoverMissing disabled={!destination()?.canUpload}
    openRequest={props.importRequest} attentionFilter={props.importAttention} destinationLabel={destination()?.label} onOpenRequestHandled={props.onImportOpened}
    label="book.import.action"
    disabledReason={language.t(destination() ? "cowork.overview.uploadReadOnly" : periodScope() && scope().periodID ? "accounting.overview.selectScope" : "cowork.enterprise.files.selectProject")}
    buttonClass="rounded-lg bg-v2-background-bg-contrast px-3 py-2 text-[11px] font-semibold text-v2-text-text-contrast disabled:cursor-not-allowed disabled:opacity-50"
    onRefresh={() => void fileActions.refetch()} />

  return (
    <div
      ref={(element) => (root = element)}
      classList={{ "min-h-full bg-v2-background-bg-deep px-4 py-7 sm:px-7 xl:px-10": !props.embedded }}
    >
      <div classList={{ "mx-auto max-w-[1500px]": !props.embedded }}>
        <Show when={!props.embedded}>
          <header class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-[30px] font-semibold tracking-[-0.025em]">{language.t("cowork.navigation.files")}</h1>
            <Show when={props.snapshotFiles === undefined}><ImportButton /></Show>
          </header>
        </Show>

        <div class={props.embedded ? "" : "mt-7"}>
          <Show when={!!activePeriod() || !flatView() || (props.embedded && props.snapshotFiles === undefined)}>
            <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
            <Show when={!activePeriod() && !flatView()}>
              <h2 class="text-[14px] font-semibold">
                {language.t("cowork.enterprise.files.folders")}
              </h2>
            </Show>
            <Show when={activePeriod()}>
              {(period) => (
                <nav
                  class="flex min-w-0 items-center gap-2 text-[11px]"
                  aria-label={language.t("cowork.navigation.files")}
                >
                  <button
                    type="button"
                    class="flex items-center gap-1.5 text-v2-text-text-muted hover:text-v2-text-text-base"
                    onClick={() => openFolder()}
                  >
                    <IconV2 name="folder" size="small" />
                    {language.t("cowork.enterprise.files.folders")}
                  </button>
                  <span class="text-v2-text-text-faint">/</span>
                  <span class="truncate font-medium text-v2-text-text-base">{period().name}</span>
                </nav>
              )}
            </Show>
            <Show when={props.embedded && props.snapshotFiles === undefined}><ImportButton /></Show>
            </div>
          </Show>
          <Show when={flatView()}>
            <FileCatalogFilters
              scopeKey={query().viewKey}
              category={state.category}
              onCategory={(category) => setState("category", category)}
              accounting={catalog()?.accounting ?? false}
              documentTypes={catalog()?.document_types ?? []}
              selectedTypes={state.documentTypes}
              onTypes={(types) => setState("documentTypes", types)}
              onClear={clearFilters}
              canClear={hasFilters()}
              shown={visibleFiles().length}
              total={catalog()?.total ?? 0}
            >
              <input
                class="h-10 w-full min-w-0 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-01 px-3 text-[12px] outline-none placeholder:text-v2-text-text-faint"
                aria-label={language.t("cowork.enterprise.files.filters.search")}
                maxLength={500}
                placeholder={language.t("cowork.enterprise.files.filters.search")}
                value={state.search}
                onInput={(event) => setState("search", event.currentTarget.value)}
              />
            </FileCatalogFilters>
          </Show>

          <Show when={selectedFiles().length || state.bulk}>
            <div class="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-v2-border-border-base bg-v2-background-bg-layer-02 px-4 py-3" role="group" aria-label={language.t("cowork.enterprise.files.actions")} data-testid="file-bulk-actions">
              <span class="text-[12px] font-medium" role="status">{language.t("cowork.enterprise.files.selectedCount", { count: state.bulk ? state.bulkTotal : selectedFiles().length })}</span>
              <Show when={state.bulk}><span role="status" class="flex items-center gap-2 text-[11px] text-v2-text-text-muted"><Spinner class="size-4" />{language.t(state.bulk === "download" ? "cowork.enterprise.workflows.reporting.downloading" : "common.loading")}<span class="tabular-nums">{state.bulkProcessed}/{state.bulkTotal}</span></span></Show>
              <div class="ml-auto flex flex-wrap items-center gap-2">
                <button type="button" class="flex min-h-9 items-center gap-2 rounded-md bg-v2-background-bg-contrast px-3 text-[12px] text-v2-text-text-contrast disabled:opacity-50" disabled={!!state.bulk || !!state.deleting || files.loading} onClick={() => void runBulk("download")}>
                  <IconV2 name="download" size="small" />{language.t("cowork.enterprise.files.download")}<span class="text-[10px] opacity-75">ZIP</span>
                </button>
                <Show when={props.snapshotFiles === undefined}><button type="button" class="flex min-h-9 items-center gap-2 rounded-md border border-v2-border-border-base bg-v2-background-bg-base px-3 text-[12px] text-v2-state-fg-danger disabled:opacity-50" disabled={!!state.bulk || !!state.deleting || files.loading} onClick={() => void runBulk("delete")}>
                  <IconV2 name="trash" size="small" />{language.t("common.delete")}
                </button></Show>
                <button type="button" class="min-h-9 px-2 text-[12px] underline underline-offset-2 disabled:opacity-50" disabled={!!state.bulk} onClick={() => setState({ selectedIDs: [], bulkFailures: [], error: "" })}>{language.t("common.clear")}</button>
              </div>
            </div>
          </Show>
          <Show when={!flatView() && looseFiles().length}>
            <label class="mb-3 flex w-fit items-center gap-2 text-[12px]"><FileSelectionCheckbox label={language.t("desktop.menu.selectAll")} checked={allSelected()} mixed={!!selectedFiles().length && !allSelected()} disabled={!!state.bulk || !!state.deleting || files.loading} onChange={toggleAll} />{language.t("desktop.menu.selectAll")}</label>
          </Show>
          <section
            aria-busy={files.loading}
            class="overflow-hidden rounded-xl border border-v2-border-border-base bg-v2-background-bg-base"
          >
            <Show when={state.error || files.error || periodError()}>
              <div
                role="alert"
                class="border-b border-v2-border-border-base bg-v2-state-bg-danger px-4 py-3 text-[11px] text-v2-state-fg-danger"
              >
                {state.error || language.t("cowork.enterprise.files.partialError")}
                <Show when={state.bulkFailures.length}><ul class="mt-1 list-inside list-disc"><For each={state.bulkFailures}>{(name) => <li class="break-all">{name}</li>}</For></ul></Show>
                <Show when={files.error || periodError()}>
                  <button
                    type="button"
                    class="ml-3 underline"
                    onClick={() => {
                      if (files.error) void Promise.resolve(fileActions.refetch()).catch(() => {})
                      if (periodError()) void Promise.resolve(periodActions.refetch()).catch(() => {})
                    }}
                  >
                    {language.t("cowork.enterprise.workflows.reporting.retry")}
                  </button>
                </Show>
              </div>
            </Show>
            <Show
              when={
                 flatView()
                   ? visibleFiles().length > 0
                  : periodGroups().length > 0 || looseFiles().length > 0
              }
              fallback={
                <Show when={!files.error && !periodError()}>
                  <DataEmpty
                    loading={files.loading || periods.loading}
                    title={language.t(
                      hasFilters() ? "prompt.popover.emptyResults" : "cowork.enterprise.files.emptyTitle",
                    )}
                    description={language.t(
                      hasFilters()
                        ? "cowork.enterprise.files.filters.emptyDescription"
                        : props.snapshotFiles !== undefined
                          ? "cowork.enterprise.files.snapshotEmpty"
                          : "cowork.enterprise.files.emptyDescription",
                    )}
                  />
                </Show>
              }
            >
              <Show
                when={flatView()}
                fallback={
                  <ul>
                    <For each={periodGroups()}>
                      {(group) => (
                        <li class="border-b border-v2-border-border-muted last:border-0">
                          <button
                            type="button"
                            class="flex w-full items-center gap-3 px-5 py-4 text-left hover:bg-v2-overlay-simple-overlay-hover"
                            onClick={() => openFolder(group)}
                          >
                            <span class="grid size-8 shrink-0 place-items-center rounded-lg bg-v2-background-bg-layer-03 text-v2-icon-icon-muted">
                              <IconV2 name="folder" size="small" />
                            </span>
                            <span class="min-w-0 flex-1">
                              <span class="block truncate text-[12px] font-semibold">{group.name}</span>
                              <span class="mt-0.5 block truncate text-[10px] text-v2-text-text-muted">
                                {group.company} · {group.project}
                              </span>
                            </span>
                            <span class="shrink-0 rounded-md bg-v2-background-bg-layer-03 px-2 py-1 text-[10px] tabular-nums">
                              {group.files.length}
                            </span>
                            <IconV2 name="outline-square-arrow" size="small" />
                          </button>
                        </li>
                      )}
                    </For>
                    <For each={looseFiles()}>
                      {(file) => (
                        <li
                          class="flex cursor-pointer items-center gap-3 border-b border-v2-border-border-muted px-5 py-4 last:border-0 hover:bg-v2-overlay-simple-overlay-hover"
                          onClick={() => viewFile(file)}
                        >
                          <span onClick={(event) => event.stopPropagation()}><FileSelectionCheckbox label={file.filename} checked={selectedSet().has(file.id)} disabled={!!state.bulk || !!state.deleting || files.loading} onChange={(checked) => toggleFile(file, checked)} /></span>
                          <FileIcon node={{ path: file.filename, type: "file" }} class="size-8 shrink-0" />
                          <div class="min-w-0 flex-1">
                            <button
                              type="button"
                              class="block max-w-full truncate text-left text-[12px] font-semibold"
                              aria-haspopup="dialog"
                            >
                              {file.filename}
                            </button>
                            <div class="mt-0.5 truncate text-[10px] text-v2-text-text-muted">
                              {file.company_name} · {file.project_name}
                            </div>
                          </div>
                          <div onClick={(event) => event.stopPropagation()}>
                            <PortfolioFileActions
                              deleting={state.deleting === file.id}
                              disabled={!!state.bulk}
                              onView={() => viewFile(file)}
                              onDownload={() => void downloadFile(file)}
                              onDelete={() => void removeFile(file)}
                            />
                          </div>
                        </li>
                      )}
                    </For>
                  </ul>
                }
              >
                <div class="overflow-x-auto">
                  <table class="w-full min-w-[680px] border-collapse text-left">
                    <thead>
                      <tr class="border-b border-v2-border-border-base text-[10px] font-semibold uppercase tracking-[0.12em] text-v2-text-text-faint">
                        <th class="w-12 pl-5 pr-2 py-3"><FileSelectionCheckbox label={language.t("desktop.menu.selectAll")} checked={allSelected()} mixed={!!selectedFiles().length && !allSelected()} disabled={!!state.bulk || !!state.deleting || files.loading} onChange={toggleAll} /></th>
                        <th class="px-5 py-3">{language.t("cowork.enterprise.files.name")}</th>
                        <th class="px-4 py-3">
                          {language.t(
                            catalog()?.accounting
                              ? "cowork.accounting.extractions.fact.documentType"
                              : "cowork.enterprise.files.type",
                          )}
                        </th>
                        <th class="px-4 py-3">{language.t("cowork.usage.status")}</th>
                        <th class="px-4 py-3">{language.t("cowork.enterprise.files.updated")}</th>
                        <th class="px-5 py-3" />
                      </tr>
                    </thead>
                    <tbody>
                      <For each={visibleFiles()}>
                        {(file) => (
                          <PortfolioFileRow
                            file={file}
                            showDocumentType={catalog()?.accounting ?? false}
                            deleting={state.deleting === file.id}
                            selected={selectedSet().has(file.id)}
                            selectionDisabled={!!state.bulk || !!state.deleting || files.loading}
                            onSelectionChange={(checked) => toggleFile(file, checked)}
                            actionsDisabled={!!state.bulk}
                            onView={() => viewFile(file)}
                            onDownload={() => void downloadFile(file)}
                            onDelete={props.snapshotFiles === undefined ? () => void removeFile(file) : undefined}
                          />
                        )}
                      </For>
                    </tbody>
                  </table>
                </div>
              </Show>
            </Show>
          </section>
          <Show when={unavailableFiles().length > 0}>
            <section class="mt-4 rounded-lg border border-v2-border-border-base p-4">
              <h3 class="text-[12px] font-medium">{language.t("cowork.enterprise.files.unavailableSnapshot")}</h3>
              <ul class="mt-2 space-y-1 text-[12px] text-v2-text-text-muted">
                <For each={unavailableFiles()}>{(file) => <li class="break-all">{file.name}</li>}</For>
              </ul>
            </section>
          </Show>
        </div>
      </div>
      <Show when={state.preview} keyed>
        {(file) => (
          <CoworkFilePreview
            file={file}
            onClose={() => {
              setState("preview", undefined)
              refresh()
            }}
            onDownload={() => void downloadFile(file)}
            downloadError={state.error}
          />
        )}
      </Show>
    </div>
  )
}

function PortfolioFileRow(props: {
  file: CoworkPortfolioFile
  showDocumentType?: boolean
  deleting: boolean
  selected: boolean
  selectionDisabled: boolean
  onSelectionChange: (checked: boolean) => void
  actionsDisabled: boolean
  onView: () => void
  onDownload: () => void
  onDelete?: () => void
}) {
  const language = useLanguage()
  return (
    <tr
      class="cursor-pointer border-b border-v2-border-border-muted last:border-0 hover:bg-v2-overlay-simple-overlay-hover"
      classList={{ "bg-v2-background-bg-layer-02": props.selected }}
      onClick={props.onView}
    >
      <td class="w-12 pl-5 pr-2 py-4" onClick={(event) => event.stopPropagation()}><FileSelectionCheckbox label={props.file.filename} checked={props.selected} disabled={props.selectionDisabled} onChange={props.onSelectionChange} /></td>
      <td class="px-5 py-4">
        <div class="flex items-start gap-2">
          <FileIcon node={{ path: props.file.filename, type: "file" }} class="size-4 shrink-0" />
          <span class="min-w-0">
            <button
              type="button"
              class="block max-w-[280px] truncate text-left text-[12px] font-medium"
              aria-haspopup="dialog"
            >
              {props.file.filename}
            </button>
            <span class="mt-0.5 block text-[10px] text-v2-text-text-muted">{formatBytes(props.file.size)}</span>
          </span>
        </div>
      </td>
      <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">
        <Show when={props.showDocumentType || (!!props.file.document_summary?.document_type && props.file.document_summary.document_type !== "unknown")}>
          <span class="block text-v2-text-text-base">
            {documentTypeLabel(props.file.document_summary?.document_type, language.t)}
          </span>
        </Show>
        <span class="uppercase" classList={{ "mt-0.5 block text-[10px]": props.showDocumentType }}>
          {fileExtension(props.file)}
        </span>
      </td>
      <td class="px-4 py-4">
        <div class="flex flex-wrap gap-1">
          <Status status={props.file.status} />
          <FileEvidenceStatus file={props.file} />
        </div>
      </td>
      <td class="px-4 py-4 text-[11px] text-v2-text-text-muted">{formatDate(props.file.updated_at)}</td>
      <td class="px-5 py-4 text-right" onClick={(event) => event.stopPropagation()}>
        <PortfolioFileActions
          deleting={props.deleting}
          disabled={props.actionsDisabled}
          onView={props.onView}
          onDownload={props.onDownload}
          onDelete={props.onDelete}
        />
      </td>
    </tr>
  )
}

function FileSelectionCheckbox(props: { label: string; checked: boolean; mixed?: boolean; disabled?: boolean; onChange: (checked: boolean) => void }) {
  return <input type="checkbox" aria-label={props.label} checked={props.checked}
    ref={(element) => createEffect(() => { element.indeterminate = !!props.mixed })}
    disabled={props.disabled} onChange={(event) => props.onChange(event.currentTarget.checked)}
    class="size-4 cursor-pointer rounded border-v2-border-border-strong accent-v2-background-bg-contrast focus-visible:outline-2 focus-visible:outline-offset-2 disabled:cursor-not-allowed disabled:opacity-50" />
}

function PortfolioFileActions(props: {
  deleting: boolean
  disabled?: boolean
  onView: () => void
  onDownload: () => void
  onDelete?: () => void
}) {
  const language = useLanguage()
  return (
    <MenuV2 gutter={6} modal={false} placement="bottom-end">
      <MenuV2.Trigger
        as={IconButtonV2}
        size="small"
        variant="ghost-muted"
        disabled={props.disabled}
        icon={<IconV2 name="outline-dots" size="small" />}
        aria-label={language.t("cowork.enterprise.files.actions")}
        title={language.t("cowork.enterprise.files.actions")}
      />
      <MenuV2.Portal>
        <MenuV2.Content>
          <MenuV2.Item onSelect={props.onView}>
            <span class="flex items-center gap-2">
              <IconV2 name="outline-square-arrow" size="small" />
              {language.t("cowork.enterprise.reports.view")}
            </span>
          </MenuV2.Item>
          <MenuV2.Item onSelect={props.onDownload}>
            <span class="flex items-center gap-2">
              <IconV2 name="download" size="small" />
              {language.t("cowork.enterprise.files.download")}
            </span>
          </MenuV2.Item>
          <Show when={props.onDelete}>
            <MenuV2.Item disabled={props.deleting} onSelect={props.onDelete}>
              <span class="flex items-center gap-2 text-v2-state-fg-danger">
                <IconV2 name="trash" size="small" />
                {language.t("common.delete")}
              </span>
            </MenuV2.Item>
          </Show>
        </MenuV2.Content>
      </MenuV2.Portal>
    </MenuV2>
  )
}

function filePeriodKey(file: CoworkPortfolioFile) {
  const periodID = file.period_id ?? file.workflow_id
  return JSON.stringify([file.company_id, file.project_id, periodID ?? null])
}

function DataEmpty(props: { title: string; description: string; loading?: boolean }) {
  return (
    <div class="grid min-h-52 place-items-center p-8 text-center">
      <Show
        when={!props.loading}
        fallback={
          <span class="size-5 animate-spin rounded-full border-2 border-v2-border-border-base border-t-v2-icon-icon-base" />
        }
      >
        <div>
          <h3 class="text-[14px] font-semibold">{props.title}</h3>
          <p class="mx-auto mt-1 max-w-md text-[11px] leading-5 text-v2-text-text-muted">{props.description}</p>
        </div>
      </Show>
    </div>
  )
}

function Status(props: { status: string; title?: string }) {
  const language = useLanguage()
  const label = () => {
    if (props.status === "uploading") return language.t("cowork.prompt.upload.uploading")
    if (props.status === "uploaded") return language.t("cowork.prompt.upload.uploaded")
    if (props.status === "error") return language.t("cowork.prompt.upload.failed")
    return language.t("cowork.enterprise.workflows.reporting.stage.unknown")
  }
  return (
    <span
      title={props.title}
      class="inline-flex rounded-md px-2 py-1 text-[10px] font-medium capitalize"
      classList={{
        "bg-v2-state-bg-success text-v2-state-fg-success": !["uploading", "error"].includes(props.status),
        "bg-v2-state-bg-warning text-v2-state-fg-warning": props.status === "uploading",
        "bg-v2-state-bg-danger text-v2-state-fg-danger": props.status === "error",
      }}
    >
      {label()}
    </span>
  )
}

function FileEvidenceStatus(props: { file: CoworkPortfolioFile }) {
  const language = useLanguage()
  const extraction = () => props.file.document_summary?.extraction_state ?? "not_extracted"
  const importKey = () => {
    const status = props.file.import_status
    if (!status || !["queued", "dispatching", "running", "applying", "review", "completed", "failed", "submission_unknown", "cancelled"].includes(status)) return
    return `book.import.${status}` as keyof typeof import("@/i18n/en").dict
  }
  return <>
    <span class="inline-flex rounded-md px-2 py-1 text-[10px] font-medium"
      classList={{
        "bg-v2-state-bg-success text-v2-state-fg-success": extraction() === "extracted",
        "bg-v2-state-bg-warning text-v2-state-fg-warning": extraction() === "partial" || extraction() === "not_extracted",
        "bg-v2-state-bg-danger text-v2-state-fg-danger": extraction() === "failed",
      }}>
      {extraction() === "extracted" ? language.t("accounting.overview.extracted")
        : extraction() === "partial" ? language.t("cowork.enterprise.workflows.reporting.extraction.partial")
        : extraction() === "failed" ? language.t("cowork.enterprise.files.filters.extractionFailed")
        : language.t("cowork.enterprise.files.filters.notExtracted")}
    </span>
    <Show when={importKey()}>{(key) =>
      <span class="inline-flex rounded-md bg-v2-background-bg-layer-02 px-2 py-1 text-[10px] text-v2-text-text-base"
        title={language.t("book.import.title")}>{language.t(key())}</span>
    }</Show>
  </>
}

function fileExtension(file: CoworkPortfolioFile) {
  return fileNameExtension(file.filename, file.content_type)
}

function fileNameExtension(filename: string, contentType: string) {
  return filename.split(".").pop()?.toLowerCase() || contentType.split("/").pop() || "file"
}

function formatBytes(value: number) {
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(value: string) {
  const date = new Date(value)
  return Number.isFinite(date.getTime())
    ? dateFormatter(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date)
    : ""
}
